// src/features/scheduling/components/LocationSearch.tsx
import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  MapPin, 
  Phone, 
  Clock, 
  DollarSign, 
  Calendar, 
  Navigation, 
  Search,
  Check,
  Building2,
  ChevronRight,
  Briefcase,
  AlertCircle
} from "lucide-react";
import type { OperatingHours, VendorLocation } from "../types";
import type { Client } from "@/api/clientApi";

// Declare google maps types
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

interface LocationSearchProps {
  vendors: VendorLocation[];
  isLoading: boolean;
  selectedVendor: VendorLocation | null;
  onSelectVendor: (vendor: VendorLocation | null) => void;
  onSearch: (zipCode: string, radius: number) => void;
  defaultZipCode?: string;
  vendorTotalsByClinicId?: Record<number, number>;
  isSuperAdmin?: boolean;
  clients?: Client[];
  selectedClientId?: number | null;
  onSelectClient?: (id: number | null) => void;
}

export default function LocationSearch({
  vendors,
  isLoading,
  selectedVendor,
  onSelectVendor,
  onSearch,
  defaultZipCode = "",
  vendorTotalsByClinicId = {},
  isSuperAdmin = false,
  clients = [],
  selectedClientId = null,
  onSelectClient = () => {},
}: LocationSearchProps) {
  const [zipCode, setZipCode] = useState(defaultZipCode);
  const [radius, setRadius] = useState(25);
  const [hasSearched, setHasSearched] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);

  // Load Google Maps Script
  useEffect(() => {
    // 1. If google already exists and has Map, we're done
    if (window.google?.maps?.Map) {
      setMapLoaded(true);
      return;
    }

    // Define global callback for Google Maps
    (window as any).initGoogleMaps = () => {
      setMapLoaded(true);
    };

    // 2. Check if a script is already loading or exists
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`);
    if (existingScript) {
      // If script exists but google.maps isn't ready, the callback will handle it
      return;
    }

    // 3. Otherwise, create and append the script
    const script = document.createElement("script");
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
    // Using callback parameter is the only guaranteed way to know when the library is ready
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps&loading=async`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      // Clean up global callback
      delete (window as any).initGoogleMaps;
    };
  }, []);

  // Initialize map when loaded
  useEffect(() => {
    // Check if map should be initialized - MUST check for .Map constructor
    if (!mapLoaded || !mapRef.current || mapInstanceRef.current || !window.google?.maps?.Map) {
      return;
    }

    try {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 39.8283, lng: -98.5795 }, // Default to USA center
        zoom: 4,
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      });
    } catch (err) {
      console.error("Failed to initialize Google Map:", err);
    }
  }, [mapLoaded]);

  // Update markers when vendors change
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    if (vendors.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();

    vendors.forEach((vendor) => {
      if (vendor.latitude && vendor.longitude) {
        const marker = new window.google.maps.Marker({
          position: { lat: vendor.latitude, lng: vendor.longitude },
          map: mapInstanceRef.current,
          title: vendor.clinicName,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: selectedVendor?.id === vendor.id ? 12 : 8,
            fillColor: selectedVendor?.id === vendor.id ? "#059669" : "#3B82F6",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
          },
        });

        marker.addListener("click", () => {
          onSelectVendor(vendor);
        });

        markersRef.current.push(marker);
        bounds.extend({ lat: vendor.latitude, lng: vendor.longitude });
      }
    });

    if (vendors.length > 0 && vendors.some((v) => v.latitude && v.longitude)) {
      mapInstanceRef.current.fitBounds(bounds);
    }
  }, [vendors, selectedVendor, mapLoaded, onSelectVendor]);

  const isValidZip = /^\d{5}$/.test(zipCode.trim());

  const handleSearch = () => {
    if (!isValidZip) return;
    setHasSearched(true);
    onSearch(zipCode.trim(), radius);
  };

  const formatOperatingHours = (hours?: Record<string, string> | OperatingHours | string | null) => {
    if (!hours) return null;
    const parsedSource: Record<string, string> | OperatingHours | null =
      typeof hours === "string"
        ? (() => {
            try {
              return JSON.parse(hours) as Record<string, string>;
            } catch {
              return null;
            }
          })()
        : hours;
    const parsed: Record<string, string> | null = parsedSource
      ? Object.fromEntries(
          Object.entries(parsedSource).filter((entry): entry is [string, string] => {
            const value = entry[1];
            return typeof value === "string";
          })
        )
      : null;
    if (!parsed) return "Hours not available";
    const today = new Date().toLocaleDateString("en-US", { weekday: "short" }).toLowerCase();
    return parsed[today] || "Hours not available";
  };

  // Format distance for display - show 2 decimal places (1.30 Miles) using car/driving distance
  const formatDistance = (distance: number | undefined): string => {
    if (!distance && distance !== 0) return "N/A";
    return `${distance.toFixed(2)} Miles`;
  };

  const getDisplayedAmount = (vendor: VendorLocation): number => {
    const fromComputed = vendorTotalsByClinicId[vendor.id];
    if (typeof fromComputed === "number" && Number.isFinite(fromComputed)) {
      return fromComputed;
    }
    return Number(vendor.price || 0);
  };

  return (
    <Card className="overflow-hidden border border-slate-200 bg-white shadow-sm dark:border-slate-700">
      <CardHeader className="border-b bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 pb-4 text-white">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
            <MapPin className="w-5 h-5" />
          </div>
          <div>
            <CardTitle className="text-lg">Find Testing Location</CardTitle>
            <p className="text-sm text-violet-100 mt-0.5">
              Search for nearby vendors by ZIP code
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4 space-y-4">
        {/* Super Admin Client Selection */}
        {isSuperAdmin && (
          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-emerald-600" />
              Select Client / Employer
            </label>
            <Select
              value={selectedClientId?.toString() || ""}
              onValueChange={(val) => onSelectClient(Number(val))}
            >
              <SelectTrigger className="h-11 bg-white dark:bg-slate-900 border-slate-300 rounded-xl">
                <SelectValue placeholder="Choose a client to see their pricing..." />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id.toString()}>
                    {client.companyName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!selectedClientId && (
              <p className="mt-2 text-xs font-medium text-amber-600 flex items-center gap-1.5 px-1">
                <AlertCircle className="w-3.5 h-3.5" />
                Select a client below to enable location search and see correct pricing.
              </p>
            )}
          </div>
        )}

        {/* Search Controls */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              ZIP Code
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={isSuperAdmin && !selectedClientId ? "Select client above..." : "Enter 5-digit ZIP code"}
                value={zipCode}
                onChange={(e) => {
                  setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5));
                  setHasSearched(false);
                  onSelectVendor(null);
                }}
                disabled={isSuperAdmin && !selectedClientId}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="pl-10 h-11"
                maxLength={5}
              />
            </div>
            {isSuperAdmin && !selectedClientId && (
              <p className="mt-1.5 text-xs font-medium text-amber-600 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                Select a client using the dropdown above first.
              </p>
            )}
            {!isValidZip && zipCode.length > 0 && !(isSuperAdmin && !selectedClientId) && (
              <p className="mt-1 text-xs text-rose-600">Enter a valid 5-digit ZIP code.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Radius
            </label>
            <Select
              value={radius.toString()}
              onValueChange={(val) => setRadius(Number(val))}
            >
              <SelectTrigger className="h-11 bg-white dark:bg-slate-900 border-slate-300">
                <SelectValue placeholder="Select radius" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 miles</SelectItem>
                <SelectItem value="10">10 miles</SelectItem>
                <SelectItem value="25">25 miles</SelectItem>
                <SelectItem value="50">50 miles</SelectItem>
                <SelectItem value="100">100 miles</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <Button
              onClick={handleSearch}
              disabled={!isValidZip || isLoading || (isSuperAdmin && !selectedClientId)}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Searching...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Navigation className="w-4 h-4" />
                  Search
                </span>
              )}
            </Button>
          </div>
        </div>

        <div
          ref={mapRef}
          className="w-full h-64 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
          style={{ minHeight: "256px" }}
        >
          {!mapLoaded && (
            <div className="w-full h-full flex items-center justify-center text-slate-500">
              <div className="text-center">
                <MapPin className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                <p className="text-sm">Loading map...</p>
              </div>
            </div>
          )}
        </div>

        {!hasSearched && (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center">
            <p className="font-medium text-slate-700">Enter ZIP and click Search to list matching clinics.</p>
          </div>
        )}

        {/* Selected Vendor Summary */}
        {selectedVendor && (
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-800 rounded-full">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-300" />
                </div>
                <div>
                  <p className="font-semibold text-green-800 dark:text-green-200">
                    {selectedVendor.clinicName}
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {selectedVendor.vendorName} • <span className="font-bold">{formatDistance(selectedVendor.distance)}</span> away
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Badge
                      className={
                        selectedVendor.appointmentRequired
                          ? "bg-amber-100 text-amber-800 border-amber-200"
                          : "bg-green-100 text-green-800 border-green-200"
                      }
                    >
                      {selectedVendor.appointmentRequired ? "Appointment Required" : "Walk-ins Welcome"}
                    </Badge>
                    <Badge
                      className={
                        selectedVendor.cocRequired
                          ? "bg-rose-100 text-rose-800 border-rose-200"
                          : "bg-slate-100 text-slate-700 border-slate-200"
                      }
                    >
                      {selectedVendor.cocRequired ? "COC Required" : "COC Not Required"}
                    </Badge>
                    {selectedVendor.acceptsMedigenixForm === false && (
                      <Badge className="bg-rose-100 text-rose-800 border-rose-200">
                        Authorization Not Accepted
                      </Badge>
                    )}
                    {selectedVendor.acceptsMedigenixForm !== false && (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                        Authorization Accepted
                      </Badge>
                    )}
                  </div>
                  {getDisplayedAmount(selectedVendor) > 0 && (
                    <div className="mt-2 text-lg font-extrabold text-blue-700 dark:text-blue-300">
                      ${getDisplayedAmount(selectedVendor).toFixed(2)}
                    </div>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSelectVendor(null)}
                className="border-green-300 text-green-700 hover:bg-green-100"
              >
                Change
              </Button>
            </div>
          </div>
        )}

        {/* Results Count */}
        {hasSearched && vendors.length > 0 && (
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
            <Building2 className="w-4 h-4" />
            <span className="text-sm font-medium">
              {vendors.length} location{vendors.length !== 1 ? "s" : ""} found
            </span>
          </div>
        )}

        {/* Vendor List */}
        {hasSearched && (
          <div className="max-h-96 overflow-y-auto space-y-3">
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="h-32 bg-slate-100 dark:bg-slate-800 animate-pulse rounded-lg"
                  />
                ))}
              </div>
            ) : vendors.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <MapPin className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No locations found</p>
                <p className="text-sm">Try a different ZIP code or radius</p>
              </div>
            ) : (
              vendors.map((vendor) => (
                <div
                  key={vendor.id}
                  onClick={() => onSelectVendor(vendor)}
                  className={`w-full p-5 rounded-xl border transition-all duration-300 cursor-pointer group ${
                    selectedVendor?.id === vendor.id
                      ? "border-violet-500 bg-violet-50/50 dark:bg-violet-900/20 ring-1 ring-violet-500 shadow-md"
                      : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-violet-400 hover:bg-slate-50/80 dark:hover:bg-slate-800/80 hover:shadow-lg"
                  }`}
                >
                  <div className="flex justify-between items-start gap-5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3">
                        <h4 className={`font-bold text-lg truncate ${
                          selectedVendor?.id === vendor.id ? "text-violet-700 dark:text-violet-300" : "text-slate-900 dark:text-slate-100"
                        }`}>
                          {vendor.clinicName}
                        </h4>
                        <Badge
                          variant="secondary"
                          className="bg-blue-600 hover:bg-blue-700 text-white font-black px-3 py-1 shrink-0 shadow-sm"
                        >
                          <Navigation className="w-3.5 h-3.5 mr-1.5" />
                          {formatDistance(vendor.distance)}
                        </Badge>
                      </div>

                      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-2">
                         <Building2 className="w-4 h-4 text-slate-400" />
                        {vendor.vendorName}
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-sm text-slate-600 dark:text-slate-400">
                        <div className="flex items-start gap-2.5">
                          <MapPin className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
                          <span className="leading-tight">
                            {vendor.address}, {vendor.city}, {vendor.state} {vendor.zipCode}
                          </span>
                        </div>

                        {vendor.phone && (
                          <div className="flex items-center gap-2.5">
                            <Phone className="w-4 h-4 text-blue-500 shrink-0" />
                            <span>{vendor.phone}</span>
                          </div>
                        )}

                        <div className="flex items-center gap-2.5 col-span-1 sm:col-span-2">
                           <Calendar className="w-4 h-4 text-emerald-500 shrink-0" />
                           <div className="flex flex-wrap items-center gap-2">
                            <Badge
                              variant="outline"
                              className={
                                vendor.appointmentRequired
                                  ? "bg-amber-50 text-amber-700 border-amber-200"
                                  : "bg-emerald-50 text-emerald-700 border-emerald-200"
                              }
                            >
                              {vendor.appointmentRequired ? "Appointment Required" : "Walk-ins Welcome"}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={
                                vendor.cocRequired
                                  ? "bg-rose-50 text-rose-700 border-rose-200"
                                  : "bg-slate-50 text-slate-600 border-slate-200"
                              }
                            >
                              {vendor.cocRequired ? "COC Required" : "COC Not Required"}
                            </Badge>
                          </div>
                        </div>

                        {vendor.operatingHours && (
                          <div className="flex items-center gap-2.5 col-span-1 sm:col-span-2">
                            <Clock className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="font-medium">Today: <span className="text-slate-900 dark:text-slate-100">{formatOperatingHours(vendor.operatingHours)}</span></span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="text-right shrink-0 flex flex-col items-end gap-2">
                      {getDisplayedAmount(vendor) > 0 && (
                        <div className="flex flex-col items-end">
                          <div className={`flex items-center justify-end gap-0.5 text-2xl font-black mb-0.5 ${
                            selectedVendor?.id === vendor.id ? "text-violet-700 dark:text-violet-300" : "text-blue-600 dark:text-blue-400"
                          }`}>
                            <DollarSign className="w-5 h-5" />
                            {getDisplayedAmount(vendor).toFixed(2)}
                          </div>
                        </div>
                      )}
                      {selectedVendor?.id === vendor.id ? (
                        <div className="bg-violet-600 rounded-full p-1.5 shadow-md mt-auto">
                          <Check className="w-5 h-5 text-white" />
                        </div>
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-violet-400 transition-colors mt-auto" />
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
